import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton, Typography, Box, Container, Card, CardContent,
  Stack, Alert, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { editorApi, AuthError } from '../api/client';

/* Read-only traffic stats (no editor session): daily visits, unique
   visitors, page loads and most-visited entry pages, from the authed
   /api/stats/traffic endpoint. */

type Traffic = Awaited<ReturnType<typeof editorApi.trafficStats>>;

const fmtDay = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('it-IT', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

const Bar: React.FC<{ value: number; max: number; color: string }> = ({
  value, max, color,
}) => (
  <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, height: 8, overflow: 'hidden' }}>
    <Box
      sx={{
        width: `${max ? (100 * value) / max : 0}%`,
        height: '100%',
        bgcolor: color,
        borderRadius: 1,
      }}
    />
  </Box>
);

const StatCard: React.FC<{ label: string; value: number; hint?: string }> = ({
  label, value, hint,
}) => (
  <Card sx={{ flex: 1, minWidth: 96 }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
      <Typography variant="h5" fontWeight={700}>
        {value.toLocaleString('it-IT')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.disabled" display="block">
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const StatsPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<Traffic | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    editorApi
      .trafficStats()
      .then((d) => alive && setData(d))
      .catch((e) => {
        if (!alive) return;
        if (e instanceof AuthError) navigate('/edit/login', { replace: true });
        else setError(e.message ?? 'errore');
      });
    return () => {
      alive = false;
    };
  }, [navigate]);

  const maxVisits = data ? Math.max(...data.days.map((d) => d.visits), 1) : 1;
  const maxPage = data?.top_pages[0]?.count ?? 1;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate('/edit')} aria-label="indietro">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>Statistiche</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 2, pb: 6 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!data && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {data && (
          <Stack spacing={3}>
            <Stack direction="row" spacing={1}>
              <StatCard label="visite" value={data.totals.visits} />
              <StatCard label="visitatori unici" value={data.totals.unique_visitors} />
              <StatCard label="pagine caricate" value={data.totals.page_loads} />
            </Stack>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Visite per giorno
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Una visita = una sessione per dispositivo (nuova dopo 30 min
                di inattività). La barra scura sono i visitatori unici.
              </Typography>
              <Stack spacing={1.25}>
                {data.days.map((d) => (
                  <Box key={d.day}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {fmtDay(d.day)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {d.visits.toLocaleString('it-IT')} visite
                        {' · '}{d.unique_visitors.toLocaleString('it-IT')} unici
                        {' · '}{d.page_loads.toLocaleString('it-IT')} pagine
                      </Typography>
                    </Stack>
                    <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                      <Bar value={d.visits} max={maxVisits} color="primary.light" />
                      <Bar value={d.unique_visitors} max={maxVisits} color="primary.dark" />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Pagine più visitate
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Solo aperture dirette (link, refresh): la navigazione interna
                all'app non passa dal server.
              </Typography>
              <Stack spacing={1}>
                {data.top_pages.map((p) => (
                  <Box key={p.path}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', mr: 1 }}
                      >
                        {p.path}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {p.count.toLocaleString('it-IT')}
                      </Typography>
                    </Stack>
                    <Bar value={p.count} max={maxPage} color="secondary.main" />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default StatsPage;
